"use client";

import { createContext, useContext, useState, ReactNode } from 'react';
import { ApplicationData } from '../models/application-data';
import { EventData } from '../types/event-data';
import { ErrorType } from '../types/error-type';
import { roundCurrency } from '../utils/helpers';


// 1. Define the type for your context data
interface UserContextType {
    user: { appData: ApplicationData } | null;
    setAppData: (appData: ApplicationData) => void;
    clearAppData: () => void;
}

// The cart is fully covered once applied credits/gift cards leave nothing owed.
// Watch item: === 0 assumes roundCurrency lands exactly on 0/-0; revisit with an epsilon if cent-level drift is ever observed.
export const isCartAmountCovered = (eventData?: EventData | null): boolean => {
    if (!eventData) return false;
    return roundCurrency(eventData.CurrentAmount - eventData.MountainCreditAmount - eventData.GiftCardAmount) === 0;
};

// Shared APS_ERROR payload for the rogue-submit guard when the cart is already fully covered.
export const buildCartCoveredError = (t: (key: string) => string): ErrorType => ({
    Error: t('CartFullyCoveredClient'),
    GuestMessage: t('CartFullyCoveredGuest'),
    StatusCode: 400,
    DateTime: new Date(),
    Detail: "",
    Instance: ""
} as ErrorType);

// 2. Create the context
export const UserContext = createContext<UserContextType | undefined>(undefined);

// 3. Create a provider component
export const UserProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<{ appData: ApplicationData } | null>(null);

    const setAppData = (appData: ApplicationData) => {
        setUser({ appData });
        console.log("app-ui::UserContext::Sent appData", appData);
    };
    const clearAppData = () => setUser(null);

    return (
        <UserContext.Provider value={{ user, setAppData, clearAppData }}>
            {children}
        </UserContext.Provider>
    );
};

// 4. Create a custom hook to consume the context
export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('app-ui::UserContext:: useUser must be used within a UserProvider');
    }
    return context;
};
